import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trustService } from '../../services/api';
import {
  Shield,
  ArrowLeft,
  Plus,
  Download,
  Edit,
  DollarSign,
  Building,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { User } from 'lucide-react';
import NewTransactionModal from '../../components/trust/NewTransactionModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import Breadcrumb from '../../components/common/Breadcrumb';

const TrustAccountDetail = () => {
  const { accountId } = useParams();
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transactions');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState({
    type: '',
    status: '',
    limit: 50
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  const handleVoidTransaction = (transaction) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Void Transaction',
      message: `Void this ${transaction.transaction_type}? The transaction will be marked as voided and the balance adjusted.`,
      variant: 'danger',
      confirmText: 'Void',
      onConfirm: async () => {
        try {
          await trustService.voidTransaction(transaction.id);
          fetchTransactions();
          fetchTrustAccountDetails();
        } catch (err) {
          showError(err.response?.data?.detail || 'Failed to void transaction');
        }
      }
    });
  };

  const handleDeleteTransaction = (transaction) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Transaction',
      message: `Permanently delete this ${transaction.transaction_type}? This cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await trustService.deleteTransaction(transaction.id);
          fetchTransactions();
          fetchTrustAccountDetails();
        } catch (err) {
          showError(err.response?.data?.detail || 'Failed to delete transaction');
        }
      }
    });
  };

  const fetchTrustAccountDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const [accountResponse, allocationsResponse] = await Promise.all([
        trustService.getTrustAccount(accountId),
        trustService.getAccountAllocations(accountId)
      ]);

      setAccount(accountResponse.data);
      setAllocations(allocationsResponse.data || []);
    } catch (error) {
      console.error('Error fetching trust account details:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const fetchTransactions = React.useCallback(async () => {
    try {
      const params = {};
      if (transactionFilters.type) params.transaction_type = transactionFilters.type;
      if (transactionFilters.status) params.status = transactionFilters.status;
      if (transactionFilters.limit) params.limit = transactionFilters.limit;

      const response = await trustService.getAccountTransactions(accountId, params);
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  }, [accountId, transactionFilters]);

  useEffect(() => {
    if (accountId) {
      fetchTrustAccountDetails();
    }
  }, [accountId, fetchTrustAccountDetails]);

  useEffect(() => {
    if (accountId && activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [accountId, activeTab, fetchTransactions]);

  const handleExport = () => {
    // Generate comprehensive export of account data
    const exportData = {
      account: {
        client_name: account.client_name,
        account_number: account.account_number,
        current_balance: account.current_balance,
        available_balance: account.available_balance,
        reserved_balance: account.reserved_balance,
        status: account.status,
        opened_date: account.opened_date
      },
      transactions: transactions,
      allocations: allocations,
      export_date: new Date().toISOString()
    };

    const csvContent = generateCSVContent(exportData);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trust-account-${account.account_number}-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Account data exported successfully!');
  };

  const generateCSVContent = (data) => {
    let csv = 'Trust Account Export\n\n';
    csv += 'Account Information\n';
    csv += 'Client Name,Account Number,Current Balance,Available Balance,Status,Opened Date\n';
    csv += `"${data.account.client_name}","${data.account.account_number}",${data.account.current_balance},${data.account.available_balance},"${data.account.status}","${data.account.opened_date}"\n\n`;

    if (data.transactions && data.transactions.length > 0) {
      csv += 'Transaction History\n';
      csv += 'Date,Type,Description,Amount,Balance After,Status\n';
      data.transactions.forEach(tx => {
        csv += `"${tx.transaction_date}","${tx.transaction_type}","${tx.description}",${tx.amount},${tx.balance_after},"${tx.status}"\n`;
      });
      csv += '\n';
    }

    if (data.allocations && data.allocations.length > 0) {
      csv += 'Case Allocations\n';
      csv += 'Case Number,Case Title,Allocated Amount,Reserved Amount,Used Amount,Available Amount\n';
      data.allocations.forEach(alloc => {
        csv += `"${alloc.case_number}","${alloc.case_title}",${alloc.allocated_amount},${alloc.reserved_amount},${alloc.used_amount},${alloc.available_amount}\n`;
      });
    }

    return csv;
  };

  const handleEdit = () => {
    setEditFormData({
      client_name: account.client_name || '',
      account_number: account.account_number || '',
      status: account.status || 'active',
      earns_interest: account.earns_interest || false,
      notes: account.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateAccount = async () => {
    try {
      setUpdatingAccount(true);

      // Prepare update data for the API
      const updateData = {
        client_name: editFormData.client_name,
        account_number: editFormData.account_number,
        status: editFormData.status,
        earns_interest: editFormData.earns_interest,
        notes: editFormData.notes
      };

      // Call the trust API to update the account
      const response = await trustService.updateTrustAccount(accountId, updateData);

      // Update local state with the response data
      setAccount(response.data);
      setShowEditModal(false);
      setEditFormData({});

      showSuccess('Account updated successfully!');

    } catch (error) {
      console.error('Error updating account:', error);
      let errorMessage = 'Failed to update account. Please try again.';

      if (error.response?.data?.detail) {
        errorMessage = `Failed to update account: ${error.response.data.detail}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid account data. Please check your inputs.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Trust account not found.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to update this account.';
      }

      showError(errorMessage);
    } finally {
      setUpdatingAccount(false);
    }
  };

  const generateAccountStatement = () => {
    const statementData = {
      account: account,
      transactions: transactions.slice(0, 25), // Last 25 transactions
      statement_date: new Date().toISOString().split('T')[0]
    };

    const statementContent = `TRUST ACCOUNT STATEMENT\n` +
      `Statement Date: ${statementData.statement_date}\n\n` +
      `CLIENT: ${account.client_name}\n` +
      `ACCOUNT NUMBER: ${account.account_number}\n` +
      `STATUS: ${account.status.toUpperCase()}\n\n` +
      `BALANCE SUMMARY\n` +
      `Current Balance: $${parseFloat(account.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Available Balance: $${parseFloat(account.available_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Reserved Balance: $${parseFloat(account.reserved_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n` +
      `RECENT TRANSACTIONS\n` +
      `Date\tType\tDescription\tAmount\tBalance\n` +
      (statementData.transactions.map(tx =>
        `${tx.transaction_date}\t${tx.transaction_type}\t${tx.description}\t$${parseFloat(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\t$${parseFloat(tx.balance_after).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      ).join('\n') || 'No recent transactions');

    const blob = new Blob([statementContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trust-statement-${account.account_number}-${statementData.statement_date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Trust account statement generated and downloaded!');
  };

  const generateInterestReport = () => {
    const interestData = {
      account: account,
      report_date: new Date().toISOString().split('T')[0],
      interest_rate: '0.10%', // Mock rate
      monthly_interest: (parseFloat(account.current_balance) * 0.001 / 12).toFixed(2),
      ytd_interest: parseFloat(account.total_interest_earned || 0).toFixed(2)
    };

    const reportContent = `IOLTA INTEREST REPORT\n` +
      `Report Date: ${interestData.report_date}\n\n` +
      `CLIENT: ${account.client_name}\n` +
      `ACCOUNT NUMBER: ${account.account_number}\n` +
      `INTEREST STATUS: ${account.earns_interest ? 'IOLTA Account' : 'Non-Interest Bearing'}\n\n` +
      `INTEREST SUMMARY\n` +
      `Current Balance: $${parseFloat(account.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Interest Rate: ${interestData.interest_rate}\n` +
      `Monthly Interest: $${interestData.monthly_interest}\n` +
      `YTD Interest Earned: $${interestData.ytd_interest}\n\n` +
      `This report is for IOLTA compliance purposes.`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iolta-interest-report-${account.account_number}-${interestData.report_date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('IOLTA interest report generated and downloaded!');
  };

  const exportTransactionHistory = () => {
    if (transactions.length === 0) {
      showWarning('No transactions to export. Please load transactions first.');
      return;
    }

    const csvContent = 'Transaction History Export\n\n' +
      'Date,Type,Description,Amount,Balance After,Status,Reference\n' +
      transactions.map(tx =>
        `"${tx.transaction_date}","${tx.transaction_type}","${tx.description}",${tx.amount},${tx.balance_after},"${tx.status}","${tx.reference_number || ''}"`
      ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaction-history-${account.account_number}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Transaction history exported successfully!');
  };

  const generateBalanceSummary = () => {
    const summaryData = {
      account: account,
      allocations: allocations,
      summary_date: new Date().toISOString().split('T')[0]
    };

    const summaryContent = `BALANCE SUMMARY REPORT\n` +
      `Report Date: ${summaryData.summary_date}\n\n` +
      `CLIENT: ${account.client_name}\n` +
      `ACCOUNT NUMBER: ${account.account_number}\n\n` +
      `BALANCE BREAKDOWN\n` +
      `Current Balance: $${parseFloat(account.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Available Balance: $${parseFloat(account.available_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Reserved Balance: $${parseFloat(account.reserved_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Total Interest Earned: $${parseFloat(account.total_interest_earned || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n` +
      (allocations.length > 0 ?
        `CASE ALLOCATIONS\n` +
        `Case\tAllocated\tUsed\tAvailable\n` +
        allocations.map(alloc =>
          `${alloc.case_number}\t$${parseFloat(alloc.allocated_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\t$${parseFloat(alloc.used_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\t$${parseFloat(alloc.available_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ).join('\n')
        : 'No case allocations found.');

    const blob = new Blob([summaryContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `balance-summary-${account.account_number}-${summaryData.summary_date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Balance summary report generated and downloaded!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Trust Account Not Found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested trust account could not be found.</p>
        <Link to="/trust-accounts" className="mt-4 inline-flex items-center text-yellow-600 hover:text-gray-600">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Trust Accounts
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'transactions', name: 'Transactions', icon: DollarSign },
    { id: 'allocations', name: 'Case Allocations', icon: Building },
    { id: 'reports', name: 'Reports', icon: TrendingUp }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            {account.client_id && (
              <Breadcrumb items={[{ label: account.client_name || 'Client', icon: User, to: `/clients/${account.client_id}` }]} />
            )}
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="mr-3 h-8 w-8 text-yellow-600" />
              {account.client_name}
            </h1>
            <p className="text-gray-600 mt-1">
              Account #{account.account_number}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowTransactionModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Transaction</span>
          </button>
          <button
            onClick={handleExport}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={handleEdit}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Account Balance Card */}
      <div className="bg-white rounded-lg shadow p-6 max-w-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Current Balance</p>
            <p className="text-2xl font-bold text-gray-900">
              {trustService.formatCurrency(account.current_balance)}
            </p>
          </div>
          <div className="p-3 bg-green-100 rounded-lg">
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <div className="mt-1">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${trustService.getAccountStatusColor(account.status)}`}>
                {account.status}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Opened Date</label>
            <p className="mt-1 text-sm text-gray-900">
              {trustService.formatDate(account.opened_date)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">IOLTA Status</label>
            <p className="mt-1 text-sm text-gray-900">
              {account.earns_interest ? 'Earns Interest' : 'Non-Interest Bearing'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'transactions' && (
            <div className="space-y-6">
              {/* Transaction Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={transactionFilters.type}
                  onChange={(e) => setTransactionFilters({...transactionFilters, type: e.target.value})}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposits</option>
                  <option value="withdrawal">Withdrawals</option>
                  <option value="fee_earned">Fees Earned</option>
                  <option value="interest">Interest</option>
                  <option value="transfer">Transfers</option>
                  <option value="refund">Refunds</option>
                </select>
                
                <select
                  value={transactionFilters.status}
                  onChange={(e) => setTransactionFilters({...transactionFilters, status: e.target.value})}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                </select>

                <select
                  value={transactionFilters.limit}
                  onChange={(e) => setTransactionFilters({...transactionFilters, limit: parseInt(e.target.value)})}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500"
                >
                  <option value={25}>25 transactions</option>
                  <option value={50}>50 transactions</option>
                  <option value={100}>100 transactions</option>
                  <option value={250}>250 transactions</option>
                </select>
              </div>

              {/* Transactions List */}
              <div className="overflow-hidden">
                {transactions.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance After</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {trustService.formatDateTime(transaction.transaction_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="mr-2">
                                {trustService.getTransactionTypeIcon(transaction.transaction_type)}
                              </span>
                              <span className={`text-sm ${trustService.getTransactionTypeColor(transaction.transaction_type)}`}>
                                {transaction.transaction_type.replace('_', ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {transaction.description}
                            {transaction.reference_number && (
                              <div className="text-xs text-gray-500">
                                Ref: {transaction.reference_number}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={parseFloat(transaction.amount) < 0 ? 'text-red-600' : 'text-green-600'}>
                              {parseFloat(transaction.amount) < 0 ? '- ' : '+ '}{trustService.formatCurrency(Math.abs(parseFloat(transaction.amount)))}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {trustService.formatCurrency(transaction.balance_after)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${trustService.getStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {transaction.status === 'completed' && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleVoidTransaction(transaction)}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                                >
                                  Void
                                </button>
                                <button
                                  onClick={() => handleDeleteTransaction(transaction)}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                            {transaction.status === 'voided' && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-400 italic">Voided</span>
                                <button
                                  onClick={() => handleDeleteTransaction(transaction)}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No transactions match your current filters.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'allocations' && (
            <div className="space-y-6">
              <h4 className="text-lg font-medium text-gray-900">Case Allocations</h4>
              {allocations.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allocated</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allocations.map((allocation) => (
                        <tr key={allocation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {allocation.case_title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {allocation.case_number}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {trustService.formatCurrency(allocation.allocated_amount)}
                          </td>
                          <td className="px-6 py-4 text-sm text-yellow-600">
                            {trustService.formatCurrency(allocation.reserved_amount)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {trustService.formatCurrency(allocation.used_amount)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-green-600">
                            {trustService.formatCurrency(allocation.available_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No case allocations</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Funds have not been allocated to any specific cases yet.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-6">
              <h4 className="text-lg font-medium text-gray-900">Available Reports</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={generateAccountStatement}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">Trust Account Statement</h5>
                  <p className="text-sm text-gray-500 mt-1">Detailed account activity for client</p>
                </button>
                <button
                  onClick={generateInterestReport}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">IOLTA Interest Report</h5>
                  <p className="text-sm text-gray-500 mt-1">Interest earnings for compliance</p>
                </button>
                <button
                  onClick={exportTransactionHistory}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">Transaction History</h5>
                  <p className="text-sm text-gray-500 mt-1">Export all transaction data</p>
                </button>
                <button
                  onClick={generateBalanceSummary}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">Balance Summary</h5>
                  <p className="text-sm text-gray-500 mt-1">Current balance breakdown</p>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Transaction Modal */}
      <NewTransactionModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        trustAccountId={accountId}
        clientId={account?.client_id}
        onTransactionCreated={() => {
          fetchTrustAccountDetails();
          if (activeTab === 'transactions') {
            fetchTransactions();
          }
        }}
      />

      {/* Edit Account Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Trust Account</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={editFormData.client_name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Client name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number *
                </label>
                <input
                  type="text"
                  value={editFormData.account_number || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, account_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editFormData.status || 'active'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="frozen">Frozen</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="earns_interest"
                  checked={editFormData.earns_interest || false}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, earns_interest: e.target.checked }))}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label htmlFor="earns_interest" className="ml-2 block text-sm text-gray-900">
                  IOLTA Account (Earns Interest)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Additional notes about this account"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={updatingAccount}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAccount}
                disabled={updatingAccount || !editFormData.client_name || !editFormData.account_number}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updatingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit className="mr-2 h-4 w-4" />
                    Update Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default TrustAccountDetail;